import { useEffect } from "react";
import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { HelpCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Button } from "~/components/ui/button";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Feature, hasFeature } from "~/lib/has-feature";
import { useCoach } from "~/contexts/coach";
import { OfferSchema } from "~/lib/schemas/offer";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { coaches, offers } from "~/lib/schemas";
import { eq } from "drizzle-orm";
import { ImageInput } from "../file-input";
import {
  FormCheckboxField,
  FormSelectField,
  FormInputField,
} from "~/components/form/form-fields";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useCallback } from "react";
import { cn } from "~/lib/cn";

type FormData = z.infer<typeof OfferSchema>;

export const getOfferData = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .validator((data: unknown) =>
    z
      .object({
        coachId: z.string().optional(),
        offerId: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<FormData> => {
    const { offerId } = data;
    const coachId = data.coachId ?? context.coach.id;

    if (!coachId) {
      throw new Error("Coach ID is required");
    }

    const { getCoach, getOffer } = await import("~/lib/server/coach");

    const coach = await getCoach(coachId);
    if (!coach) {
      throw new Error("Coach not found");
    }

    let offer;
    if (offerId) {
      offer = await getOffer(offerId);
    } else if (coach.mainOfferId) {
      offer = await getOffer(coach.mainOfferId);
    } else {
      const { getDBWithInit } = await import("~/lib/db");
      const newOffer = await (
        await getDBWithInit()
      )
        .insert(offers)
        .values({
          coachId,
          headline: "My Main Offer",
          preHeadline: "Introducing",
          description: "This is my main coaching offer.",
        })
        .returning()
        .then((res) => res[0]);

      await (await getDBWithInit())
        .update(coaches)
        .set({ mainOfferId: newOffer.id })
        .where(eq(coaches.id, coachId));

      offer = await getOffer(newOffer.id);
    }

    if (!offer) {
      throw new Error("Offer not found");
    }

    return {
      username: coach.username,
      ...offer,
      applicationDeadline:
        offer.applicationDeadline &&
        new Intl.DateTimeFormat("en-CA").format(offer.applicationDeadline),
      cohortStartDate:
        offer.cohortStartDate &&
        new Intl.DateTimeFormat("en-CA").format(offer.cohortStartDate),
      price: offer.price ? offer.price / 100 : undefined,
      originalPrice: offer.originalPrice
        ? offer.originalPrice / 100
        : undefined,
      benefits: offer.benefits.map((b) => ({
        ...b,
        _delete: false,
      })),
      tags: offer.tags.map((t) => ({
        ...t,
        _delete: false,
      })),
      testimonials: coach.testimonials.map((t) => ({
        ...t,
        _delete: false,
      })),
      components: offer.components.map((c) => ({
        ...c,
        _delete: false,
      })),
      bonuses: offer.bonuses.map((b) => ({
        ...b,
        _delete: false,
      })),
      faqs: offer.faqs.map((f) => ({
        ...f,
        _delete: false,
      })),
    };
  });

interface EntityOperations<T, U> {
  create: (item: T) => Promise<U>;
  update: (id: string, item: Partial<T>) => Promise<U>;
  delete: (id: string) => Promise<U>;
}

function processEntities<T extends { id?: string; _delete?: boolean }, R, U>(
  items: T[] | undefined,
  operations: EntityOperations<R, U>,
  prepareItem: (item: T, index: number) => R,
): Promise<U[]> {
  if (!items?.length) {
    return Promise.resolve([]);
  }

  return Promise.all(
    items.map((item, index) => {
      if (item._delete && item.id) {
        return operations.delete(item.id);
      }
      if (!item.id) {
        return operations.create(prepareItem(item, index));
      } else {
        return operations.update(item.id, prepareItem(item, index));
      }
    }),
  );
}

export const saveOffer = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator((data: unknown) =>
    z
      .object({
        values: OfferSchema,
        coachId: z.string(),
        offerId: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { values, coachId: _coachId, offerId } = data;

    const priceInCents = values.price
      ? Math.round(values.price * 100)
      : undefined;
    const originalPriceInCents = values.originalPrice
      ? Math.round(values.originalPrice * 100)
      : undefined;

    const offerData = {
      ...values,
      price: priceInCents,
      originalPrice: originalPriceInCents,
      applicationDeadline: values.applicationDeadline
        ? new Date(values.applicationDeadline)
        : null,
      cohortStartDate: values.cohortStartDate
        ? new Date(values.cohortStartDate)
        : null,
    };

    const {
      updateOffer,
      createOfferBenefit,
      updateOfferBenefit,
      deleteOfferBenefit,
      createOfferTag,
      updateOfferTag,
      deleteOfferTag,
      createOfferComponent,
      updateOfferComponent,
      deleteOfferComponent,
      createOfferBonus,
      updateOfferBonus,
      deleteOfferBonus,
      createOfferFaq,
      updateOfferFaq,
      deleteOfferFaq,
    } = await import("~/lib/server/coach");

    const { createTestimonial, updateTestimonial, deleteTestimonial } =
      await import("~/lib/server/testimonial");

    await updateOffer(offerId || values.id, offerData);

    await Promise.all([
      // Benefits
      processEntities(
        values.benefits,
        {
          create: createOfferBenefit,
          update: updateOfferBenefit,
          delete: deleteOfferBenefit,
        },
        (item, index) => ({ ...item, offerId: values.id, order: index }),
      ),

      // Tags
      processEntities(
        values.tags,
        {
          create: createOfferTag,
          update: updateOfferTag,
          delete: deleteOfferTag,
        },
        (item, index) => ({ ...item, offerId: values.id, order: index }),
      ),

      // Testimonials
      processEntities(
        values.testimonials,
        {
          create: createTestimonial,
          update: updateTestimonial,
          delete: deleteTestimonial,
        },
        (item, index) => ({ ...item, coachId: values.coachId, order: index }),
      ),

      // Components
      processEntities(
        values.components,
        {
          create: createOfferComponent,
          update: updateOfferComponent,
          delete: deleteOfferComponent,
        },
        (item, index) => ({ ...item, offerId: values.id, order: index }),
      ),

      // Bonuses
      processEntities(
        values.bonuses,
        {
          create: createOfferBonus,
          update: updateOfferBonus,
          delete: deleteOfferBonus,
        },
        (item, index) => ({ ...item, offerId: values.id, order: index }),
      ),

      // FAQs
      processEntities(
        values.faqs,
        {
          create: createOfferFaq,
          update: updateOfferFaq,
          delete: deleteOfferFaq,
        },
        (item, index) => ({ ...item, offerId: values.id, order: index }),
      ),
    ]);

    return {
      status: "success",
      message: "Offer saved",
      data: await getOfferData({
        data: { coachId: values.coachId, offerId: values.id },
      }),
    };
  });

export const OfferForm = ({
  initialData,
  title,
}: {
  initialData: FormData;
  title: string;
}) => {
  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    control,
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(OfferSchema),
  });

  const { errors, isDirty } = formState;

  // Use reset to update form data instead of defaultValues to prevent re-sync loops
  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const queryClient = useQueryClient();

  const saveOfferMutation = useMutation({
    mutationFn: (values: FormData) =>
      saveOffer({
        data: {
          values,
          coachId: values.coachId,
          offerId: values.id,
        },
      }),
    onSuccess: async (result) => {
      toast.success("Offer saved successfully");

      // Invalidate broader coach cache to update dependent data
      await queryClient.invalidateQueries({ queryKey: ["coach"] });

      // Use fresh data returned from server and reset form to get new IDs and remove deleted items
      const freshData = result.data;

      // Reset form with fresh data from server
      reset(freshData);
    },
    onError: () => {
      toast.error("Failed to save offer");
    },
  });

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && 0 < Object.keys(errors).length,
  );

  const handleAiFillField = (fieldName: keyof FormData) => (value: string) => {
    setValue(fieldName, value);
  };

  // Helper functions for array management
  const addTag = useCallback(() => {
    const current = getValues("tags") || [];
    setValue(
      "tags",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          title: "",
          property: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveTag = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("tags") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("tags", updated, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  const addBenefit = useCallback(() => {
    const current = getValues("benefits") || [];
    setValue(
      "benefits",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          title: "",
          content: "",
          imageSrc: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveBenefit = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("benefits") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("benefits", updated, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  const addTestimonial = useCallback(() => {
    const current = getValues("testimonials") || [];
    setValue(
      "testimonials",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          content: "",
          author: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveTestimonial = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("testimonials") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("testimonials", updated, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, setValue],
  );

  const addComponent = useCallback(() => {
    const current = getValues("components") || [];
    setValue(
      "components",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          preHeader: "",
          title: "",
          imageUrl: "",
          content: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveComponent = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("components") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("components", updated, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  const addBonus = useCallback(() => {
    const current = getValues("bonuses") || [];
    setValue(
      "bonuses",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          title: "",
          content: "",
          imageSrc: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveBonus = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("bonuses") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("bonuses", updated, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  const addFaq = useCallback(() => {
    const current = getValues("faqs") || [];
    setValue(
      "faqs",
      [
        ...current,
        {
          _delete: false,
          id: undefined,
          question: "",
          answer: "",
          order: current.length,
        },
      ],
      { shouldDirty: true },
    );
  }, [getValues, setValue]);

  const moveFaq = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = getValues("faqs") || [];
      if (toIndex < 0 || toIndex >= current.length) return;
      const updated = [...current];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      updated.forEach((item, idx) => {
        item.order = idx;
      });
      setValue("faqs", updated, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue],
  );

  const coach = useCoach();
  const hasOfferPageFeature = coach
    ? hasFeature(coach, Feature.OfferPage)
    : false;

  const onSubmit = (data: FormData) => {
    saveOfferMutation.mutate(data);
  };

  const handleInvalidSubmit = (errors: typeof formState.errors) => {
    console.error("Form validation errors:", errors);
    const errorMessages = Object.entries(errors)
      .map(([field, error]) => `${field}: ${error?.message || "Invalid"}`)
      .join(", ");
    toast.error(`Form validation failed: ${errorMessages}`);
  };

  // Watch field arrays
  const tags = watch("tags") || [];
  const benefits = watch("benefits") || [];
  const testimonials = watch("testimonials") || [];
  const components = watch("components") || [];
  const bonuses = watch("bonuses") || [];
  const faqs = watch("faqs") || [];

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />
      <PristineInvalidAlert
        show={pristineAlert.show}
        onDiscard={pristineAlert.onDiscard}
        onKeepEditing={pristineAlert.onKeepEditing}
      />
      <form
        onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
        className="space-y-8"
      >
        <ProfileEditHeader
          title={title}
          description="Edit your offer. Publicly shown on your profile."
          form={formState}
          username={initialData.username!}
        />

        {hasOfferPageFeature && (
          <Card>
            <CardHeader>
              <CardTitle className="flex gap-2">
                <HelpCircle className="text-brand-700 size-[1em]" /> How your
                offer is presented
              </CardTitle>
            </CardHeader>
            <CardContent className="prose">
              <p>
                On your main profile page, we show your offer <em>headline</em>,{" "}
                <em>description</em> and an overview of the <em>benefits</em>{" "}
                (just the titles).
              </p>
              <p>
                The rest of the details are shown on the full offer page, which
                has the following structure:
              </p>
              <ul>
                <li>About</li>
                <li>Tags</li>
                <li>Challenges / Struggles</li>
                <li>Results from following this program</li>
                <li>About the Program (practical details)</li>
                <li>Benefits of this program</li>
                <li>Testimonials</li>
                <li>Bonuses</li>
                <li>Satisfaction Guarantee</li>
                <li>FAQs</li>
                <li>Final call to action</li>
              </ul>
              <p>
                You're free to leave fields empty when you don't want that
                section to be visible on the offer page.
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input {...register("id")} type="hidden" />
            <input {...register("coachId")} type="hidden" />
            <input {...register("username")} type="hidden" />

            {hasOfferPageFeature && (
              <>
                <FormField
                  label="VSL (Video Sales Letter) Video URL"
                  helpText="We're working on letting you directly upload videos. For now, you can use a service like YouTube or Vimeo and paste the video URL here."
                  error={errors.vslVideoUrl?.message}
                >
                  <Input {...register("vslVideoUrl")} type="url" />
                </FormField>

                <FormField
                  label="Pre-Headline"
                  error={errors.preHeadline?.message}
                >
                  <Input {...register("preHeadline")} />
                </FormField>
              </>
            )}

            <FormField
              label="Headline: benefit-centric statement about your main coaching offer"
              error={errors.headline?.message}
              onAiFillField={handleAiFillField("headline")}
            >
              <Input {...register("headline")} />
            </FormField>
            <FormField
              label="Description of the coaching offer"
              error={errors.description?.message}
            >
              <Textarea {...register("description")} maxLength={300} />
            </FormField>

            <FormField
              label="Application Deadline (if applicable)"
              error={errors.applicationDeadline?.message}
            >
              <Input {...register("applicationDeadline")} type="date" />
            </FormField>

            <FormField
              label="Cohort Start Date (if applicable)"
              error={errors.cohortStartDate?.message}
            >
              <Input {...register("cohortStartDate")} type="date" />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
            <CardDescription>
              Set up pricing if you want to sell this directly instead of
              through a discovery call.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormCheckboxField
              control={control}
              name="directSaleEnabled"
              label="Enable direct purchase"
            />

            {watch("directSaleEnabled") && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Price" error={errors.price?.message}>
                    <Input
                      {...register("price", { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter price"
                    />
                  </FormField>
                  <FormField label="Currency" error={errors.currency?.message}>
                    <select
                      {...register("currency")}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </FormField>
                </div>
                <FormField
                  label="Original Price (optional, for showing discount)"
                  error={errors.originalPrice?.message}
                >
                  <Input
                    {...register("originalPrice", { valueAsNumber: true })}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter original price"
                  />
                </FormField>
              </div>
            )}
          </CardContent>
        </Card>

        {hasOfferPageFeature && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Challenges / Struggles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  label="Image shown on the left side"
                  error={errors.strugglesImageUrl?.message}
                >
                  <ImageInput
                    {...register("strugglesImageUrl")}
                    type="url"
                    value={watch("strugglesImageUrl") || ""}
                    recommendedDimensions={{ width: 440, height: 660 }}
                  />
                </FormField>

                <FormField
                  label="Headline"
                  error={errors.strugglesHeadline?.message}
                >
                  <Input {...register("strugglesHeadline")} />
                </FormField>
                <FormField
                  label="Main Text"
                  error={errors.strugglesDescription?.message}
                >
                  <Textarea
                    {...register("strugglesDescription")}
                    maxLength={500}
                  />
                </FormField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Results from following this program</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  label="Results Pre-Headline"
                  error={errors.resultsPreHeadline?.message}
                >
                  <Input {...register("resultsPreHeadline")} />
                </FormField>
                <FormField
                  label="Results Headline"
                  error={errors.resultsHeadline?.message}
                >
                  <Input {...register("resultsHeadline")} />
                </FormField>
                <FormField
                  label="Description"
                  error={errors.resultsDescription?.message}
                >
                  <Textarea
                    {...register("resultsDescription")}
                    maxLength={500}
                  />
                </FormField>
                <FormField
                  label="Image"
                  error={errors.resultsImageUrl?.message}
                >
                  <ImageInput
                    {...register("resultsImageUrl")}
                    type="url"
                    value={watch("resultsImageUrl") || ""}
                    recommendedDimensions={{ width: 600, height: 400 }}
                  />
                </FormField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>About the Program (practical details)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  label="Program Headline"
                  error={errors.programHeadline?.message}
                >
                  <Input {...register("programHeadline")} />
                </FormField>
                <FormField
                  label="Program Description"
                  error={errors.programDescription?.message}
                >
                  <Textarea
                    {...register("programDescription")}
                    maxLength={500}
                  />
                </FormField>
                <FormField
                  label="Image to show in the program overview section"
                  helpText="(300x200)"
                  error={errors.programImageUrl?.message}
                >
                  <ImageInput
                    {...register("programImageUrl")}
                    type="url"
                    value={watch("programImageUrl") || ""}
                  />
                </FormField>

                <h3 className="font-heading text-primary text-lg">
                  Program components / parts
                </h3>

                <Button
                  type="button"
                  variant="outline"
                  className="mb-4"
                  onClick={addComponent}
                >
                  <Plus /> Add Component
                </Button>

                <div className="space-y-4">
                  {components.map((component, index) => {
                    const componentKey = component.id || `component-${index}`;
                    return (
                      <div
                        key={componentKey}
                        className={cn(
                          "group w-full space-y-4 p-4 border rounded",
                          {
                            "opacity-50": component._delete,
                          },
                        )}
                      >
                        {component.id && (
                          <input
                            {...register(`components.${index}.id`)}
                            type="hidden"
                          />
                        )}

                        <div className="flex w-full items-center justify-between gap-2">
                          <FormCheckboxField
                            control={control}
                            name={`components.${index}._delete`}
                            label="Delete"
                          />
                          <div className="flex space-x-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => moveComponent(index, index - 1)}
                              disabled={index === 0}
                              title="Move Up"
                            >
                              <ArrowUp className="size-[1em]" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => moveComponent(index, index + 1)}
                              disabled={index === components.length - 1}
                              title="Move Down"
                            >
                              <ArrowDown className="size-[1em]" />
                            </Button>
                          </div>
                        </div>

                        <FormInputField
                          control={control}
                          name={`components.${index}.preHeader`}
                          label="Pre-Header"
                        />

                        <FormInputField
                          control={control}
                          name={`components.${index}.title`}
                          label="Title"
                          required
                        />

                        <FormField
                          label="Image"
                          error={errors.components?.[index]?.imageUrl?.message}
                        >
                          <ImageInput
                            {...register(`components.${index}.imageUrl`)}
                            type="url"
                            value={watch(`components.${index}.imageUrl`) || ""}
                            recommendedDimensions={{
                              width: 600,
                              height: 400,
                            }}
                          />
                        </FormField>

                        <FormField
                          label="Content"
                          error={errors.components?.[index]?.content?.message}
                        >
                          <Textarea
                            {...register(`components.${index}.content`)}
                            maxLength={500}
                          />
                        </FormField>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Tags */}
        <Card>
          <CardHeader>
            <CardTitle>Tags</CardTitle>
            <p className="text-text-secondary text-sm">
              We recommend that you highlight key practical properties of your
              offer, for example "12 weeks" or "1:1".
            </p>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              className="mb-4"
              onClick={addTag}
            >
              <Plus /> Add Tag
            </Button>

            <div className="space-y-4">
              {tags.map((tag, index) => {
                const tagKey = tag.id || `tag-${index}`;
                return (
                  <div
                    key={tagKey}
                    className={cn("group w-full space-y-4 p-4 border rounded", {
                      "opacity-50": tag._delete,
                    })}
                  >
                    {tag.id && (
                      <input {...register(`tags.${index}.id`)} type="hidden" />
                    )}

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`tags.${index}._delete`}
                        label="Delete"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveTag(index, index - 1)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveTag(index, index + 1)}
                          disabled={index === tags.length - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormInputField
                      control={control}
                      name={`tags.${index}.title`}
                      label="Title"
                      required
                    />

                    <FormSelectField
                      control={control}
                      name={`tags.${index}.property`}
                      label="Property"
                      options={[
                        "Number of sessions",
                        "Program duration",
                        "Session duration",
                        "Session format",
                        "Group size",
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Benefits of this program</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              type="button"
              variant="outline"
              className="mb-4"
              onClick={addBenefit}
            >
              <Plus /> Add Benefit
            </Button>

            <div className="space-y-4">
              {benefits.map((benefit, index) => {
                const benefitKey = benefit.id || `benefit-${index}`;
                return (
                  <div
                    key={benefitKey}
                    className={cn("group w-full space-y-4 p-4 border rounded", {
                      "opacity-50": benefit._delete,
                    })}
                  >
                    {benefit.id && (
                      <input
                        {...register(`benefits.${index}.id`)}
                        type="hidden"
                      />
                    )}

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`benefits.${index}._delete`}
                        label="Delete"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveBenefit(index, index - 1)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveBenefit(index, index + 1)}
                          disabled={index === benefits.length - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormInputField
                      control={control}
                      name={`benefits.${index}.title`}
                      label="Title"
                      required
                    />

                    {hasOfferPageFeature && (
                      <>
                        <FormField
                          label="Content"
                          error={errors.benefits?.[index]?.content?.message}
                        >
                          <Textarea
                            {...register(`benefits.${index}.content`)}
                            maxLength={500}
                          />
                        </FormField>
                        <FormField
                          label="Image"
                          error={errors.benefits?.[index]?.imageSrc?.message}
                        >
                          <ImageInput
                            type="url"
                            {...register(`benefits.${index}.imageSrc`)}
                            value={watch(`benefits.${index}.imageSrc`) || ""}
                            recommendedDimensions={{
                              width: 600,
                              height: 400,
                            }}
                          />
                        </FormField>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Guarantee */}
        {hasOfferPageFeature && (
          <Card>
            <CardHeader>
              <CardTitle>Satisfaction Guarantee</CardTitle>
              <CardDescription>
                If you offer a satisfaction guarantee, describe it here and
                we'll show it on your offer page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                label="Satisfaction Guarantee Text"
                error={errors.satisfactionGuaranteeText?.message}
              >
                <Textarea
                  {...register("satisfactionGuaranteeText")}
                  maxLength={500}
                />
              </FormField>
            </CardContent>
          </Card>
        )}

        {/* Testimonials */}
        <Card>
          <CardHeader>
            <CardTitle>Testimonials</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              className="mb-4"
              onClick={addTestimonial}
            >
              <Plus /> Add Testimonial
            </Button>

            <div className="space-y-4">
              {testimonials.map((testimonial, index) => {
                const testimonialKey = testimonial.id || `testimonial-${index}`;
                return (
                  <div
                    key={testimonialKey}
                    className={cn("group w-full space-y-4 p-4 border rounded", {
                      "opacity-50": testimonial._delete,
                    })}
                  >
                    {testimonial.id && (
                      <input
                        {...register(`testimonials.${index}.id`)}
                        type="hidden"
                      />
                    )}

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`testimonials.${index}._delete`}
                        label="Delete"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveTestimonial(index, index - 1)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveTestimonial(index, index + 1)}
                          disabled={index === testimonials.length - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormField
                      label="Content"
                      error={errors.testimonials?.[index]?.content?.message}
                    >
                      <Textarea
                        {...register(`testimonials.${index}.content`)}
                        maxLength={500}
                      />
                    </FormField>

                    <FormInputField
                      control={control}
                      name={`testimonials.${index}.author`}
                      label="Author"
                      required
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Bonuses */}
        {hasOfferPageFeature && (
          <Card>
            <CardHeader>
              <CardTitle>Bonuses</CardTitle>
              <p className="text-secondary text-sm">
                Add any bonuses included with your offer.
              </p>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="outline"
                className="mb-4"
                onClick={addBonus}
              >
                <Plus /> Add Bonus
              </Button>

              <div className="space-y-4">
                {bonuses.map((bonus, index) => {
                  const bonusKey = bonus.id || `bonus-${index}`;
                  return (
                    <div
                      key={bonusKey}
                      className={cn(
                        "group w-full space-y-4 p-4 border rounded",
                        {
                          "opacity-50": bonus._delete,
                        },
                      )}
                    >
                      {bonus.id && (
                        <input
                          {...register(`bonuses.${index}.id`)}
                          type="hidden"
                        />
                      )}

                      <div className="flex w-full items-center justify-between gap-2">
                        <FormCheckboxField
                          control={control}
                          name={`bonuses.${index}._delete`}
                          label="Delete"
                        />
                        <div className="flex space-x-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveBonus(index, index - 1)}
                            disabled={index === 0}
                            title="Move Up"
                          >
                            <ArrowUp className="size-[1em]" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveBonus(index, index + 1)}
                            disabled={index === bonuses.length - 1}
                            title="Move Down"
                          >
                            <ArrowDown className="size-[1em]" />
                          </Button>
                        </div>
                      </div>

                      <FormInputField
                        control={control}
                        name={`bonuses.${index}.title`}
                        label="Title"
                        required
                      />

                      <FormField
                        label="Content"
                        error={errors.bonuses?.[index]?.content?.message}
                      >
                        <Textarea
                          {...register(`bonuses.${index}.content`)}
                          maxLength={500}
                        />
                      </FormField>

                      <FormField
                        label="Image"
                        error={errors.bonuses?.[index]?.imageSrc?.message}
                      >
                        <ImageInput
                          type="url"
                          {...register(`bonuses.${index}.imageSrc`)}
                          value={watch(`bonuses.${index}.imageSrc`) || ""}
                          recommendedDimensions={{
                            width: 600,
                            height: 400,
                          }}
                        />
                      </FormField>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>FAQs</CardTitle>
            <CardDescription>Add frequently asked questions</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button
              type="button"
              variant="outline"
              className="self-start mb-4"
              onClick={addFaq}
            >
              <Plus /> Add FAQ
            </Button>

            <div className="space-y-4">
              {faqs.map((faq, index) => {
                const faqKey = faq.id || `faq-${index}`;
                return (
                  <div
                    key={faqKey}
                    className={cn("group w-full space-y-4 p-4 border rounded", {
                      "opacity-50": faq._delete,
                    })}
                  >
                    {faq.id && (
                      <input {...register(`faqs.${index}.id`)} type="hidden" />
                    )}

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`faqs.${index}._delete`}
                        label="Delete"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFaq(index, index - 1)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFaq(index, index + 1)}
                          disabled={index === faqs.length - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormInputField
                      control={control}
                      name={`faqs.${index}.question`}
                      label="Question"
                      required
                    />

                    <FormField
                      label="Answer"
                      error={errors.faqs?.[index]?.answer?.message}
                    >
                      <Textarea
                        {...register(`faqs.${index}.answer`)}
                        maxLength={500}
                      />
                    </FormField>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Final call to action</CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              label="Bottom CTA Headline"
              error={errors.bottomCtaHeadline?.message}
            >
              <Input {...register("bottomCtaHeadline")} />
            </FormField>
            <FormField
              label="Bottom CTA Text"
              error={errors.bottomCtaText?.message}
            >
              <Textarea {...register("bottomCtaText")} maxLength={500} />
            </FormField>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saveOfferMutation.isPending}>
            {saveOfferMutation.isPending ? "Saving..." : "Save Offer"}
          </Button>
        </div>
      </form>
    </>
  );
};
